import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { effaiTheme } from './theme/theme';
import { Layout } from './components/AppLayout';
import { ToastContainer } from './components/toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { HomePage } from './pages/HomePage';
import { UsersPage } from './pages/UsersPage';
import { FieldOptionsPage } from './pages/FieldOptionsPage';
import { EnergyVectorsPage } from './pages/EnergyVectorsPage';
import { EnergyUsePage } from './pages/EnergyUsePage';
import { CustomersPage } from './pages/CustomersPage';
import { CustomerDetailPage } from './pages/CustomerDetailPage';
import { SiteDetailPage } from './pages/SiteDetailPage';
import { DiagnosisPage } from './pages/DiagnosisPage';
import { DiagnosisDetailPage } from './pages/DiagnosisDetailPage';
import { IncidenceEneaPage } from './pages/IncidenceEneaPage';
import { AiSearchPage } from './pages/AiSearchPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import InvitePage from './pages/InvitePage';
import './App.css';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/it';
import { CircularProgress, Box } from '@mui/material';
import { HttpLoadingBar } from './components/common/HttpLoadingBar';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider theme={effaiTheme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
          <AuthProvider>
            <Box sx={{ position: 'relative' }}>
              <HttpLoadingBar />
              <Router>
                <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/invite" element={<InvitePage />} />

                {/* Protected routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <HomePage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <UsersPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customers"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CustomersPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customers/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CustomerDetailPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/customers/:id/siti"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CustomerDetailPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sites/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <SiteDetailPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sites/:id/immobile"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <SiteDetailPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sites/:id/confini"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <SiteDetailPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/diagnosis"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <DiagnosisPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/diagnosis/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <DiagnosisDetailPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/field-options"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <FieldOptionsPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/energy-vectors"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <EnergyVectorsPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/energy-use"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <EnergyUsePage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/incidence-enea"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <IncidenceEneaPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ai-search"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <AiSearchPage />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                </Routes>
                <ToastContainer />
              </Router>
            </Box>
          </AuthProvider>
        </LocalizationProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
